// src/pages/dashboard/admin/disputes/Disputes.jsx
import { useState, useEffect } from 'react';
import api from '../../../../lib/api';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import Button from '../../../../components/layout/ui/Button';
import { formatMoney, formatDate } from '../../../../lib/helpers';
import { 
  AlertCircle, User, FileText, Clock, CheckCircle, 
  MessageSquare, Image as ImageIcon, X 
} from 'lucide-react';
import { useConfirm } from '../../../../hooks/useConfirm';
import { error, success } from '../../../../components/common/Toast';

export default function AdminDisputes() {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [status, setStatus] = useState('resolved');
  const [showLightbox, setShowLightbox] = useState(null);

  const { confirm } = useConfirm();

  useEffect(() => {
    fetchDisputes();
  }, []);

  const fetchDisputes = async () => {
    try {
      const res = await api.get('/admin/disputes');
      setDisputes(res.data.data.data || []);
    } catch {
      error('Failed to load disputes');
    } finally {
      setLoading(false);
    }
  };

  const resolveDispute = async () => {
    if (!resolutionNotes.trim()) return error('Please add resolution notes');

    const confirmed = await confirm({
      title: "Resolve This Dispute?",
      message: "This will mark the dispute as " + status + ". This action cannot be undone.",
      confirmText: "Yes, Resolve",
      cancelText: "Cancel",
      danger: status === 'rejected',
    });

    if (!confirmed) return;

    try {
      await api.post(`/admin/disputes/${selectedDispute.id}/resolve`, {
        resolution_notes: resolutionNotes,
        status: status,
      });
      success('Dispute resolved successfully!');
      setSelectedDispute(null);
      setResolutionNotes('');
      fetchDisputes();
    } catch {
      error('Failed to resolve dispute');
    }
  };

  const getFiles = (filePath) => {
    if (!filePath) return [];
    try {
      const paths = JSON.parse(filePath);
      return paths.map(p => ({
        url: `${process.env.REACT_APP_IMAGE_URL}storage/${p.replace(/\\/g, '/')}`,
        thumb: `${process.env.REACT_APP_IMAGE_URL}storage/${p.replace(/\\/g, '/')}`
      }));
    } catch {
      return [];
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div className="max-w-7xl mx-auto py-12 px-6">
      <h1 className="text-5xl font-black text-center mb-16">Dispute Resolution Center</h1>

      {disputes.length === 0 ? (
        <div className="text-center py-32 bg-gray-50 rounded-3xl">
          <AlertCircle size={120} className="mx-auto text-gray-300 mb-8" />
          <p className="text-3xl text-gray-600">No active disputes</p>
        </div>
      ) : (
        <div className="space-y-12">
          {disputes.map((dispute) => {
            const files = getFiles(dispute.file_path);
            const appeal = dispute.appeal;

            return (
              <div key={dispute.id} className="bg-white rounded-3xl shadow-2xl overflow-hidden border-l-12 border-red-500">
                <div className="p-10 lg:p-16">
                  {/* Header */}
                  <div className="flex flex-col lg:flex-row justify-between items-start gap-8 mb-10">
                    <div className="flex items-center gap-8">
                      <div className="w-20 h-20 bg-red-100 rounded-3xl flex items-center justify-center">
                        <AlertCircle size={48} className="text-red-600" />
                      </div>
                      <div>
                        <h3 className="text-3xl font-bold">
                          {dispute.contract.nanny.first_name + " " + dispute.contract.nanny.last_name} vs {dispute.contract.customer.first_name + " " + dispute.contract.customer.last_name}
                        </h3>
                        <p className="text-xl text-gray-600">Contract #{dispute.contract_id} • {formatMoney(dispute.contract.monthly_salary)}/mo</p>
                        <p className="text-gray-500 mt-2">{formatDate(dispute.created_at)}</p>
                      </div>
                    </div>

                    <div className="text-right">
                      <span className={`inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-2xl shadow-lg
                        ${dispute.status === 'open' ? 'bg-yellow-100 text-yellow-800' :
                          dispute.status === 'in_review' ? 'bg-purple-100 text-purple-800' :
                          'bg-green-100 text-green-800'}`}>
                        {dispute.status === 'open' && <Clock size={36} />}
                        {dispute.status === 'in_review' && <AlertCircle size={36} />}
                        {dispute.status === 'resolved' && <CheckCircle size={36} />}
                        {dispute.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* Dispute Details */}
                  <div className="bg-gray-50 p-10 rounded-3xl mb-8">
                    <h4 className="text-2xl font-bold mb-4">Dispute Details</h4>
                    <p className="text-xl leading-relaxed text-gray-800 whitespace-pre-line">
                      {dispute.dispute_details}
                    </p>
                  </div>

                  {/* Evidence Images */}
                  {files.length > 0 && (
                    <div className="mb-8">
                      <h4 className="text-2xl font-bold mb-6">Evidence ({files.length})</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {files.map((file, i) => (
                          <button
                            key={i}
                            onClick={() => setShowLightbox(file.url)}
                            className="relative group overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition"
                          >
                            <img src={file.thumb} alt={`Evidence ${i + 1}`} className="w-full h-48 object-cover" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                              <ImageIcon size={40} className="text-white" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Appeal */}
                 {/* Appeal with Images */}
{appeal && (
  <div className="bg-purple-50 p-10 rounded-3xl border-4 border-purple-300 mb-12">
    <h4 className="text-3xl font-bold text-purple-800 mb-6 flex items-center gap-5">
      <MessageSquare size={40} /> Appeal Filed
    </h4>

    {/* Appeal Text */}
    <p className="text-xl text-purple-700 leading-relaxed whitespace-pre-line mb-8">
      {appeal.dispute_details}
    </p>

    {/* Appeal Images */}
    {appeal.file_path && (() => {
      let appealFiles = [];
      try {
        appealFiles = JSON.parse(appeal.file_path).map(p => 
          `${process.env.REACT_APP_IMAGE_URL}storage/${p.replace(/\\/g, '/')}`
        );
      } catch (e) {}

      return appealFiles.length > 0 ? (
        <div>
          <h5 className="text-xl font-bold text-purple-800 mb-5">Appeal Evidence ({appealFiles.length})</h5>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {appealFiles.map((url, i) => (
              <button
                key={i}
                onClick={() => setShowLightbox(url)}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition"
              >
                <img 
                  src={url} 
                  alt={`Appeal evidence ${i + 1}`} 
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                  <ImageIcon size={40} className="text-white" />
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : null;
    })()}
  </div>
)}

                  {/* Resolve Button */}
                  {dispute.status !== 'resolved' && (
                    <div className="text-center">
                      <Button
                        onClick={() => setSelectedDispute(dispute)}
                        className="px-16 py-6 text-2xl"
                      >
                        Resolve This Dispute
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {showLightbox && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setShowLightbox(null)}>
          <button className="absolute top-8 right-8 text-white hover:bg-white/10 rounded-full p-4 transition">
            <X size={48} />
          </button>
          <img src={showLightbox} alt="Evidence" className="max-w-full max-h-full rounded-2xl shadow-2xl" />
        </div>
      )}

      {/* Resolve Modal */}
      {selectedDispute && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelectedDispute(null)}>
          <div className="bg-white rounded-3xl max-w-4xl w-full p-10" onClick={e => e.stopPropagation()}>
            <h2 className="text-4xl font-black mb-8">Resolve Dispute #{selectedDispute.id}</h2>

            <div className="space-y-8">
              <div>
                <label className="text-2xl font-bold">Resolution Outcome</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full mt-3 px-6 py-5 rounded-2xl border-2 text-xl"
                >
                  <option value="resolved">Resolved (Fair)</option>
                  <option value="rejected">Rejected (Unfounded)</option>
                </select>
              </div>

              <div>
                <label className="text-2xl font-bold block mb-3">Resolution Notes</label>
                <textarea
                  rows={8}
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Explain your decision in detail..."
                  className="w-full px-6 py-5 rounded-2xl border-2 text-lg focus:ring-4 focus:ring-primary/20"
                />
              </div>

              <div className="flex justify-end gap-6">
                <Button variant="outline" onClick={() => setSelectedDispute(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={resolveDispute}
                  className={`text-xl px-12 py-5 ${
                    status === 'rejected'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {status === 'resolved' ? 'Mark as Resolved' : 'Reject Dispute'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}